import { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useLogout, useCurrentUser } from "utils/hooks";
import { updatePracticeSubmissionAction } from "store/actions/submissions";
import { addPatientAction } from "store/actions/patients";
import useOrganizationSettings, {
  getPatientWordCapitalized,
} from "utils/hooks/organizations/useOrganizationSettings";

type ReassignPatientToTherapistProps = {
  submissionId?: string | undefined;
};
type Props = {
  therapistId: string | null;
  patientId?: string | null;
};

const useReassignPatientToTherapist = ({
  submissionId,
}: ReassignPatientToTherapistProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["common"]);
  const { logOut } = useLogout();
  const [assigning, setAssigning] = useState(false);
  const { user } = useCurrentUser();
  const { settings: orgSettings } = useOrganizationSettings();

  const reassignPatientToTherapist = async ({
    therapistId,
    patientId,
  }: Props) => {
    if (!therapistId) return null;

    setAssigning(true);

    const result = async () => {
      return await fetch(`${process.env.REACT_APP_API_URL}`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
          query: `
                mutation {
                    reassignPatientToTherapist(
                        therapistId: "${therapistId}",
                        submissionId: "${submissionId || ""}",
                        patientId: "${patientId || ""}"
                    ) {
                      patient {
                        uuid
                        firstName
                        lastName
                        email
                        slug
                        createdAt
                        color
                        icon
                        alias
                        defaultReminder
                        notificationTimes
                        type
                        profile {
                          dob
                          gender
                          status
                          idNumber
                        }
                      }
                      submission {
                        uuid
                        createdAt
                        slug
                        userId
                        authorId
                        assignmentId
                        assignmentLabel
                        privacy
                        color
                        isDraft
                        groupId
                        groupName
                        groupPrivacy
                        isWideData
                        category
                        hideFromPatient
                        userName
                        userEmail
                        therapistId
                        connectedTherapistId
                        userNameOrEmail
                        isNegative
                        sources
                        icon {
                          name
                          src
                        }
                        responseFields {
                          uuid
                          submissionId
                          sectionId
                          sectionLabel
                          sectionOrder
                          fieldId
                          fieldLabel
                          fieldType
                          value
                          includeInScoreCalculation
                        }
                        submissionScore {
                          scoringInstructions
                          scoringType
                          totalScore
                          color
                          fieldsetScores {
                            name
                            score
                            scoringType
                          }
                          scoreDisplays {
                              id
                              name
                              min
                              max
                              color
                              order
                          }
                          scoreSuffix
                          scoreAsPercentage
                          minPossibleScore
                          maxPossibleScore
                        }
                      }
                    }
                    }
                  `,
        }),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          organizationId: user?.activeOrganizationId || "NA",
          permissionKey: "add_patients",
        },
      })
        .then(async (res) => {
          const resData = await res.json();

          if (res.status !== 200 && res.status !== 201) {
            if (
              resData.errors &&
              resData.errors[0] &&
              resData.errors[0].message.includes("BRKN_6001")
            ) {
              toast.warn(t("errors.login_again_error"));
              logOut();
            } else if (
              resData.errors[0]?.message.includes("PATIENT_LIMIT_REACHED")
            ) {
              toast.warn(
                t("errors.patient_limit_error", {
                  patientWordCapitalized:
                    getPatientWordCapitalized(orgSettings),
                })
              );
              return false;
            } else {
              toast.error(t("errors.something_went_wrong"));
              setAssigning(false);
              throw new Error(t("errors.failed_error"));
            }
          }
          return resData;
        })
        .then((resData) => {
          if (resData.errors) {
            toast.error(`${t("errors.something_went_wrong")}`);
            setAssigning(false);
            return false;
          } else {
            dispatch(
              addPatientAction(resData.data.reassignPatientToTherapist.patient)
            );
            if (resData.data.reassignPatientToTherapist.submission) {
              dispatch(
                updatePracticeSubmissionAction(
                  resData.data.reassignPatientToTherapist.submission
                )
              );
            }
            setAssigning(false);
            toast(
              t("inbox.assign_patient_to_therapist_success", {
                patientWordCapitalized: getPatientWordCapitalized(orgSettings),
              })
            );
            return true;
          }
        })
        .catch((error) => {
          console.log(error);
          setAssigning(false);
          return false;
        });
    };

    return await result();
  };

  return {
    reassignPatientToTherapist,
    assigning,
  };
};

export default useReassignPatientToTherapist;
